import type { EngagementEventInput } from "@mvk/shared";
import { isApiEnabled } from "../api/config";
import { visitorAuthStorage } from "../api/authStorage";
import { apiRequest } from "../api/client";
import { scanApi } from "../api/scan";
import { appConfig } from "../config";
import {
  idbAddOutbox,
  idbDeleteOutbox,
  idbListPendingOutbox,
  idbUpdateOutbox,
  type OutboxItem,
} from "./offlineDb";
import { debugLog } from "./debugLog";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `obx-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

let flushing = false;

export const outboxService = {
  async enqueueScan(input: {
    eventId: string;
    codeValue: string;
    codeType: "qr" | "barcode";
    scannedAt: string;
    idempotencyKey?: string;
  }): Promise<string> {
    const idempotencyKey = input.idempotencyKey ?? uuid();
    const item: OutboxItem = {
      id: uuid(),
      type: "scan",
      eventId: input.eventId,
      idempotencyKey,
      payload: {
        codeValue: input.codeValue,
        codeType: input.codeType,
        scannedAt: input.scannedAt,
      },
      createdAt: new Date().toISOString(),
      attempts: 0,
      status: "pending",
    };
    await idbAddOutbox(item);
    void this.flush();
    return idempotencyKey;
  },

  async enqueueEngagement(eventId: string, events: EngagementEventInput[]): Promise<void> {
    if (!events.length) return;
    const item: OutboxItem = {
      id: uuid(),
      type: "engagement",
      eventId,
      idempotencyKey: events[0]!.idempotencyKey,
      payload: { events },
      createdAt: new Date().toISOString(),
      attempts: 0,
      status: "pending",
    };
    await idbAddOutbox(item);
    void this.flush();
  },

  async flush(): Promise<void> {
    if (flushing) return;
    if (!appConfig.featureFlags.enableOfflineMode) return;
    if (!isApiEnabled() || !visitorAuthStorage.isAuthenticated()) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;

    flushing = true;
    try {
      const pending = await idbListPendingOutbox();
      for (const item of pending) {
        try {
          if (item.type === "scan") {
            const p = item.payload as {
              codeValue: string;
              codeType: "qr" | "barcode";
            };
            await scanApi.create(item.eventId, p.codeValue, p.codeType, item.idempotencyKey);
          } else if (item.type === "engagement") {
            const p = item.payload as { events: EngagementEventInput[] };
            await apiRequest(`/events/${item.eventId}/engagement/batch`, {
              method: "POST",
              body: { events: p.events },
              audience: "visitor",
            });
          }
          await idbDeleteOutbox(item.id);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          debugLog.warn("offline", "outbox item failed", { id: item.id, message });
          item.attempts += 1;
          item.lastError = message;
          item.status = item.attempts >= 8 ? "failed" : "pending";
          await idbUpdateOutbox(item);
        }
      }
    } finally {
      flushing = false;
    }
  },

  startAutoFlush(): () => void {
    const onOnline = () => void this.flush();
    const onFocus = () => void this.flush();
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onFocus);
    const timer = window.setInterval(() => void this.flush(), 30_000);
    void this.flush();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(timer);
    };
  },
};
